import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { QrCode, Search } from '@mui/icons-material';
import { BrowserMultiFormatReader } from '@zxing/browser';
import {
  BarcodeFormat,
  DecodeHintType,
  NotFoundException,
} from '@zxing/library';

const bs3 = {
  btnPrimary: {
    bgcolor: '#337ab7',
    color: '#fff',
    border: '1px solid #2e6da4',
    borderRadius: '4px',
    px: 2,
    py: 0.8,
    fontSize: '13px',
    fontWeight: 400,
    textTransform: 'none',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    '&:hover': { bgcolor: '#286090', borderColor: '#204d74' },
    '&:disabled': { bgcolor: '#337ab7', opacity: 0.65 },
  },
  btnDefault: {
    bgcolor: '#fff',
    color: '#333',
    border: '1px solid #ccc',
    borderRadius: '4px',
    px: 2,
    py: 0.8,
    fontSize: '13px',
    fontWeight: 400,
    textTransform: 'none',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    '&:hover': { bgcolor: '#e6e6e6', borderColor: '#adadad' },
  },
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      '& fieldset': { borderColor: '#ccc' },
      '&:hover fieldset': { borderColor: '#66afe9' },
      '&.Mui-focused fieldset': {
        borderColor: '#66afe9',
        boxShadow: 'inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102,175,233,.6)',
      },
    },
  },
  alertDanger: {
    bgcolor: '#f2dede',
    color: '#a94442',
    border: '1px solid #ebccd1',
    borderRadius: '4px',
    p: 1.5,
    fontSize: '13px',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  alertInfo: {
    bgcolor: '#d9edf7',
    color: '#31708f',
    border: '1px solid #bce8f1',
    borderRadius: '4px',
    p: 1.5,
    fontSize: '13px',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
};

/**
 * CameraDialog — Scanner kamera inline untuk scan barcode/QR.
 *
 * Props:
 *   open            : boolean — apakah scanner aktif
 *   onScan          : (code: string) => void — callback saat barcode berhasil di-scan
 *   onNotification  : (severity: string, message: string) => void — callback notifikasi
 *   continuousMode  : boolean (default: true) — mode continuous scanning (kamera tetap menyala)
 */
const CameraDialog = ({ open, onScan, onNotification, continuousMode = true }) => {
  const [cameraError, setCameraError] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [lastScannedCode, setLastScannedCode] = useState('');

  const cameraVideoRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const zxingReaderRef = useRef(null);
  const scanHandledRef = useRef(false);
  const manualBarcodeInputRef = useRef(null);

  // Start/stop camera scanning when scanner enabled/disabled
  useEffect(() => {
    if (!open) return undefined;

    // Check camera support
    const cameraAccessSupported =
      typeof window !== 'undefined' && navigator?.mediaDevices?.getUserMedia;
    if (!cameraAccessSupported) {
      setCameraError(
        'Browser ini belum mendukung akses kamera. Gunakan input manual di bawah.'
      );
      return undefined;
    }

    let cancelled = false;
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.ITF,
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.PDF_417,
      BarcodeFormat.AZTEC,
    ]);
    hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new BrowserMultiFormatReader(hints);
    zxingReaderRef.current = reader;
    scanHandledRef.current = false;
    setScanning(true);
    setCameraError('');

    const start = async () => {
      // Small delay to ensure video element is mounted
      await new Promise((r) => setTimeout(r, 180));
      if (cancelled || !cameraVideoRef.current) return;

      try {
        const onDecode = (result, err) => {
          if (cancelled || scanHandledRef.current) return;
          if (err && !(err instanceof NotFoundException)) {
            console.warn('ZXing scan:', err);
          }
          if (!result) return;

          const text = result.getText()?.trim();
          if (!text) return;

          setLastScannedCode(text);
          scanHandledRef.current = true;
          onScan(text);

          setTimeout(() => {
            scanHandledRef.current = false;
          }, continuousMode ? 150 : 500);
        };

        let controls = null;
        try {
          controls = await reader.decodeFromVideoDevice(undefined, cameraVideoRef.current, onDecode);
        } catch (primaryErr) {
          const devices = await BrowserMultiFormatReader.listVideoInputDevices();
          const prioritizedDevice = devices.find((d) =>
            /(back|rear|environment)/i.test(d.label || '')
          );
          const fallbackDevice = devices.length ? devices[devices.length - 1] : undefined;
          const selectedDeviceId = prioritizedDevice?.deviceId || fallbackDevice?.deviceId;
          controls = await reader.decodeFromVideoDevice(selectedDeviceId, cameraVideoRef.current, onDecode);
        }

        if (cancelled) {
          controls.stop();
          return;
        }
        zxingControlsRef.current = controls;
        setCameraError('');
      } catch (e) {
        if (!cancelled) {
          setCameraError(
            e?.message ||
              'Gagal memulai kamera / pemindaian. Gunakan input manual atau periksa izin kamera.'
          );
          setScanning(false);
        }
      }
    };

    start();

    // Focus the manual input as fallback after small delay
    setTimeout(() => manualBarcodeInputRef.current?.focus(), 500);

    return () => {
      cancelled = true;
      scanHandledRef.current = false;
      setScanning(false);
      try {
        zxingControlsRef.current?.stop();
      } catch (e) {
        /* ignore */
      }
      zxingControlsRef.current = null;
      try {
        reader.reset();
      } catch (e) {
        /* ignore */
      }
      zxingReaderRef.current = null;
    };
  }, [open, onScan, continuousMode]);

  const handleManualSubmit = () => {
    const code = manualCode.trim();
    if (!code) {
      if (onNotification) {
        onNotification('warning', 'Masukkan kode barang terlebih dahulu.');
      }
      return;
    }
    
    // Update last scanned code
    setLastScannedCode(code);
    
    // Clear input
    setManualCode('');
    
    // Trigger callback
    onScan(code);
    
  };

  return (
    <Box
      sx={{
        border: '1px solid #ddd',
        borderRadius: '4px',
        bgcolor: '#fff',
        mt: 1.5,
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      }}
    >
      <Box
        sx={{
          bgcolor: '#337ab7',
          color: '#fff',
          fontSize: '15px',
          fontWeight: 700,
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCode sx={{ fontSize: 20 }} />
          <span>Scanner Kamera Aktif</span>
          <Box
            sx={{
              ml: 2,
              px: 1,
              py: 0.3,
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {continuousMode ? '🔄 Continuous' : '📸 Single'}
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* Camera Video Feed */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 520,
            mx: 'auto',
            height: { xs: 220, md: 260 },
            bgcolor: '#000',
            borderRadius: '4px',
            overflow: 'hidden',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <video
            ref={cameraVideoRef}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            autoPlay
            playsInline
            muted
          />
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '82%',
              height: '42%',
              border: '2px solid rgba(92, 184, 92, 0.9)',
              borderRadius: '6px',
              pointerEvents: 'none',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.18)',
            }}
          />
          {scanning && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: '#fff',
                px: 1,
                py: 0.3,
                borderRadius: '4px',
                fontSize: '11px',
              }}
            >
              <CircularProgress size={12} sx={{ color: '#5cb85c' }} />
              <span>Scanning...</span>
            </Box>
          )}
        </Box>

        {/* Error message */}
        {cameraError && (
          <Box sx={{ ...bs3.alertDanger, mb: 2 }}>
            {cameraError}
          </Box>
        )}

        {/* Last Scanned Result */}
        {lastScannedCode && (
          <Box sx={{ ...bs3.alertInfo, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>Hasil Scan Terakhir:</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, mt: 0.3 }}>{lastScannedCode}</Typography>
            </Box>
            <Button
              onClick={() => setLastScannedCode('')}
              size="small"
              sx={{
                fontSize: '11px',
                color: '#31708f',
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(49, 112, 143, 0.1)' },
              }}
            >
              Clear
            </Button>
          </Box>
        )}

        {/* Info */}
        <Box sx={{ ...bs3.alertInfo, mb: 2 }}>
          {continuousMode 
            ? 'Mode Continuous - Kamera tetap menyala. Arahkan barcode ke kamera untuk scan berulang kali tanpa menutup dialog.'
            : 'Mode Single - Dialog akan menutup otomatis setelah scan berhasil.'}
        </Box>

        {/* Manual barcode input */}
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 700,
            color: '#333',
            mb: 0.5,
            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          }}
        >
          Input Manual
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Ketik kode barang manual..."
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            inputRef={manualBarcodeInputRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleManualSubmit();
              }
            }}
            sx={bs3.formControl}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <QrCode sx={{ fontSize: 18, color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            onClick={handleManualSubmit}
            startIcon={<Search sx={{ fontSize: 16 }} />}
            sx={bs3.btnPrimary}
          >
            Cari
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CameraDialog;
